/**
 * Shared contractor extraction and deduplication helpers for Safety Observation.
 *
 * Handles raw API responses containing Maker user records:
 * [
 *   { id: 1066, name: "MBLLP", role: "MAKER" },
 *   { id: 1276, name: "MBLLP", role: "MAKER" }
 * ]
 *
 * In such responses:
 * - id: Maker USER ID (NOT a contractor ID)
 * - name: Contractor / Company name
 * - role: "MAKER"
 *
 * These helpers guarantee:
 * 1. Safe extraction of clean contractor names.
 * 2. Case- and whitespace-insensitive deduplication while preserving canonical display text.
 * 3. Ignoring invalid/blank names without creating blank options.
 * 4. Never confusing Maker user IDs with genuine contractor IDs.
 * 5. Preservation of genuine contractor IDs when present (e.g. contractor_id, company_id).
 */

/**
 * Extracts a clean display contractor name from a contractor or maker object/string.
 * Returns empty string if missing or whitespace only.
 */
export const getContractorName = (contractor) => {
  if (contractor == null) return "";

  if (typeof contractor === "string") {
    return contractor.trim();
  }

  if (typeof contractor === "object") {
    const candidate =
      contractor.name ||
      contractor.contractor_name ||
      contractor.company ||
      contractor.company_name ||
      contractor.contractor?.name ||
      contractor.contractor?.contractor_name ||
      "";

    return String(candidate || "").trim();
  }

  return "";
};

/**
 * Checks if an ID represents a genuine contractor/company ID rather than a Maker user ID.
 * Returns genuine contractor ID if present, otherwise null.
 */
export const getContractorId = (contractor) => {
  if (!contractor || typeof contractor !== "object") return null;

  // 1. Explicit contractor ID fields (highest precedence)
  const genuineId =
    contractor.contractor_id ??
    contractor.company_id ??
    contractor.contractor?.id ??
    contractor.contractor?.contractor_id ??
    contractor.company?.id ??
    null;

  if (genuineId != null && String(genuineId).trim() !== "") {
    return genuineId;
  }

  // 2. If the record has role === "MAKER" or explicit user attributes without explicit contractor_id,
  // "id" is likely the Maker user's ID, so avoid confusing it as contractor ID.
  const isMakerUserRecord =
    (contractor.role === "MAKER" || contractor.username != null || contractor.email != null) &&
    contractor.contractor_id == null;

  if (isMakerUserRecord) {
    return null;
  }

  // 3. Fallback: if record has an id (e.g. from /contractors/ API response { id, name })
  if (contractor.id != null) {
    return contractor.id;
  }

  return null;
};

/**
 * Checks if a project name or project object corresponds to Arkade-NEST (retained for backward compatibility)
 */
export const isArkadeNestProject = (projectOrName) => {
  if (!projectOrName) return false;
  const name =
    typeof projectOrName === "string"
      ? projectOrName
      : projectOrName?.name ||
        projectOrName?.project_name ||
        projectOrName?.title ||
        "";
  const clean = String(name).trim();
  if (!clean) return false;
  return (
    (/(?=.*arkade)(?=.*nest)/i.test(clean)) ||
    /^nest$/i.test(clean)
  );
};

/**
 * Transforms raw contractor / Maker records into a deduplicated list of unique contractor options.
 * Completely dynamic for all projects and contractors.
 *
 * @param {Array} list - Raw records from API
 * @param {Object|string|boolean} [options] - Optional options object
 * @returns {Array<{ contractorName: string, normalizedName: string, contractorId: string|number|null, raw: any }>}
 */
export const getUniqueContractorOptions = (list = [], options = {}) => {
  if (!Array.isArray(list) && !options) return [];

  const rawList = Array.isArray(list) ? [...list] : [];
  const seen = new Set();
  const uniqueOptions = [];

  for (const item of rawList) {
    const rawName = getContractorName(item);

    // Skip empty or whitespace-only names
    if (!rawName) continue;

    const normalizedKey = rawName.toLowerCase();
    if (seen.has(normalizedKey)) {
      // If we previously saw this contractor without a contractorId but current item has one, update it
      const existing = uniqueOptions.find((opt) => opt.normalizedName === normalizedKey);
      if (existing && !existing.contractorId) {
        const candidateId = getContractorId(item);
        if (candidateId) {
          existing.contractorId = candidateId;
        }
      }
      continue;
    }

    seen.add(normalizedKey);

    const genuineContractorId = getContractorId(item);
    const isInternal = Boolean(
      item?.is_internal ||
      item?.is_developer ||
      item?.isInternal ||
      item?.raw?.is_internal ||
      item?.raw?.is_developer
    );

    uniqueOptions.push({
      contractorName: rawName,
      normalizedName: normalizedKey,
      contractorId: genuineContractorId,
      isInternal,
      raw: item,
    });
  }

  uniqueOptions.sort((a, b) =>
    a.contractorName.localeCompare(b.contractorName)
  );

  return uniqueOptions;
};

/**
 * Resolves the primary internal contractor / developer option for a project.
 *
 * Precedence:
 * 1. Explicit internal / developer contractor marked by the backend API (is_internal: true)
 * 2. Organization or developer company name match from project / user profile metadata
 * 3. Dynamic resolution for projects whose internal developer organization is registered in contractor list
 *
 * Returns matching contractor option or null (allowing fallback to "Assigned to Internal Team").
 *
 * @param {Array} contractorOptions - Deduplicated contractor options
 * @param {Object} [context] - Context containing orgName, projectName, projectId, profile, etc.
 * @returns {Object|null}
 */
export const resolveInternalContractorOption = (
  contractorOptions = [],
  context = {},
) => {
  if (!Array.isArray(contractorOptions) || contractorOptions.length === 0) {
    return null;
  }

  // 1. Explicit internal / developer contractor from backend API response
  const explicit = contractorOptions.find(
    (opt) => opt.isInternal || opt.raw?.is_internal || opt.raw?.is_developer,
  );
  if (explicit) {
    return explicit;
  }

  // 2. Check context organization name or profile contractor name
  const orgName = String(
    context?.orgName ||
      context?.organizationName ||
      context?.profile?.org_name ||
      context?.profile?.contractor_name ||
      "",
  )
    .trim()
    .toLowerCase();

  if (orgName) {
    const matched = contractorOptions.find(
      (opt) => opt.normalizedName === orgName,
    );
    if (matched) {
      return matched;
    }
  }

  // 3. Dynamic resolution for Arkade Nest (Project ID 165 / Arkade - Nest)
  const isNest = Boolean(
    context?.isArkadeNest ||
      isArkadeNestProject(context?.projectName) ||
      isArkadeNestProject(context?.project) ||
      String(context?.projectId || "") === "165",
  );

  if (isNest) {
    const arkadeOption = contractorOptions.find(
      (opt) =>
        opt.normalizedName === "arkade developers limited" ||
        (opt.normalizedName.includes("arkade") &&
          (opt.normalizedName.includes("limited") ||
            opt.normalizedName.includes("developer"))),
    );
    if (arkadeOption) {
      return arkadeOption;
    }
  }

  return null;
};

/**
 * Resolves the display label for the internal team option in the contractor dropdown.
 *
 * Rules:
 * 1. For External users, returns "Assigned to Internal Team".
 * 2. For Internal users belonging to Arkade Nest project, returns "Arkade Developers Limited".
 * 3. For Internal users belonging to other projects, returns that project's configured
 *    organization name (dynamically from project object, user profile, or context).
 * 4. If no configured organization name is available, falls back safely to "Assigned to Internal Team".
 *
 * @param {Object} [context] - Context with userType, profile, projectName, projectId, project, projects, etc.
 * @returns {string} - The display label for the internal team dropdown option.
 */
export const resolveInternalTeamDisplayLabel = (context = {}) => {
  const profile = context?.profile || context?.userProfile || null;

  // 1. Check user type - External users keep the default label
  const rawUserType = String(
    context?.userType ||
      context?.user_type ||
      profile?.user_type ||
      profile?.userType ||
      "",
  )
    .trim()
    .toUpperCase();

  if (rawUserType === "EXTERNAL") {
    return "Assigned to Internal Team";
  }

  // 2. Identify target project details
  const targetProjectId =
    context?.projectId ??
    context?.project_id ??
    context?.project?.id ??
    context?.project?.project_id ??
    profile?.active_project_id ??
    profile?.project_id ??
    null;

  let activeProject =
    context?.project && typeof context.project === "object"
      ? context.project
      : null;

  if (!activeProject && targetProjectId && Array.isArray(context?.projects)) {
    activeProject =
      context.projects.find(
        (p) =>
          String(p?.id ?? p?.project_id ?? "").trim() ===
          String(targetProjectId).trim(),
      ) || null;
  }

  let projectCandidateName =
    context?.projectName ??
    context?.project_name ??
    activeProject?.name ??
    activeProject?.project_name ??
    "";

  if (
    !projectCandidateName &&
    targetProjectId &&
    Array.isArray(profile?.accesses)
  ) {
    const matchingAccess = profile.accesses.find(
      (a) =>
        String(a?.project_id ?? a?.project?.id ?? a?.project ?? "").trim() ===
        String(targetProjectId).trim(),
    );
    if (matchingAccess) {
      projectCandidateName =
        matchingAccess.project_name ||
        matchingAccess.name ||
        matchingAccess.project?.name ||
        "";
    }
  }

  // 3. Arkade Nest check
  const isNest = Boolean(
    context?.isArkadeNest ||
      isArkadeNestProject(projectCandidateName) ||
      isArkadeNestProject(activeProject) ||
      (targetProjectId && String(targetProjectId).trim() === "165"),
  );

  if (isNest) {
    return "Arkade Developers Limited";
  }

  // 4. Resolve configured organization name from active project, context, or profile
  const candidateOrg = String(
    activeProject?.organization_name ||
      activeProject?.org_name ||
      (typeof activeProject?.organization === "string"
        ? activeProject.organization
        : activeProject?.organization?.name) ||
      activeProject?.company_name ||
      context?.orgName ||
      context?.organizationName ||
      context?.organization_name ||
      profile?.organization_name ||
      profile?.org_name ||
      profile?.org?.name ||
      profile?.organization?.name ||
      "",
  ).trim();

  if (
    candidateOrg &&
    candidateOrg.toLowerCase() !== "internal" &&
    candidateOrg.toLowerCase() !== "assigned to internal team" &&
    candidateOrg !== "—" &&
    candidateOrg !== "-"
  ) {
    return candidateOrg;
  }

  // 5. Safe fallback when no organization data is found
  return "Assigned to Internal Team";
};

/**
 * Authoritatively resolves the display Organization / Contractor name for a user.
 *
 * Applicable across Permit to Work, Safety Observation, Safety Inspection, and other modules.
 *
 * Rules & Precedence:
 * 1. If user has a configured external contractor / company name (e.g. "MBLLP", "Beenee Fire",
 *    "Rangam Interior"), return it directly.
 * 2. If user is Internal or has no contractor selected, do NOT default to "Internal".
 *    Check if the user belongs to a project organization/group (e.g. Arkade-Nest -> "Arkade NEST").
 * 3. If the user or active project has an explicit organization/company name (e.g. from user profile,
 *    project metadata, or organization service), use that display name.
 * 4. Case 1: If user belongs to Arkade-Nest (by project name "Arkade - Nest" / "Arkade-Nest",
 *    project ID 165, or matching user access), return "Arkade NEST".
 * 5. Case 5: If a user genuinely has no organization, contractor, project organization,
 *    or group association, return the canonical fallback: "Internal".
 * 6. Case 6: If the user has access to multiple projects, resolve using the active project's
 *    context (projectId / projectName / matching project access).
 *
 * @param {Object} [userProfile] - Logged-in user profile (from resolveSafetyUserProfile() or getUserData())
 * @param {Object} [context] - Context containing projectId, projectName, project, orgName, activeAccess, etc.
 * @returns {string} - Resolved organization or contractor name
 */
export const resolveUserOrganizationOrContractor = (
  userProfile = null,
  context = {},
) => {
  const profile = userProfile || {};

  // 1. Check for genuine external contractor name on user profile (CASE 2, 3, 4)
  const candidateContractor = String(
    profile?.contractor_name ||
      profile?.contractor ||
      profile?.company_name ||
      "",
  ).trim();

  const isExplicitInternalContractor =
    candidateContractor.toLowerCase() === "internal" ||
    candidateContractor.toLowerCase() === "assigned to internal team";

  if (candidateContractor && !isExplicitInternalContractor) {
    return candidateContractor;
  }

  // 2. Identify the active project context for the current workflow (CASE 6: Multi-project safety)
  const targetProjectId =
    context?.projectId ??
    context?.project_id ??
    context?.project?.id ??
    profile?.active_project_id ??
    profile?.project_id ??
    null;

  let projectCandidateName =
    context?.projectName ??
    context?.project_name ??
    context?.project?.name ??
    context?.project?.project_name ??
    "";

  // If not provided in context, attempt to locate project name in user's matching access for targetProjectId
  if (!projectCandidateName && targetProjectId && Array.isArray(profile?.accesses)) {
    const matchingAccess = profile.accesses.find(
      (a) =>
        String(a?.project_id ?? a?.project?.id ?? a?.project).trim() ===
        String(targetProjectId).trim(),
    );
    if (matchingAccess) {
      projectCandidateName =
        matchingAccess.project_name ||
        matchingAccess.name ||
        matchingAccess.project?.name ||
        "";
    }
  }

  // 3. CASE 1: Check if user belongs to Arkade-Nest project or organization
  const isNest =
    Boolean(context?.isArkadeNest) ||
    isArkadeNestProject(projectCandidateName) ||
    isArkadeNestProject(context?.project) ||
    String(targetProjectId || "").trim() === "165";

  if (isNest) {
    return "Arkade NEST";
  }

  // 4. Check explicit organization name from profile, context, or project
  const explicitOrg = String(
    context?.orgName ||
      context?.organizationName ||
      context?.organization_name ||
      context?.project?.organization_name ||
      context?.project?.org_name ||
      profile?.organization_name ||
      profile?.org_name ||
      profile?.org?.name ||
      profile?.organization?.name ||
      "",
  ).trim();

  if (explicitOrg && explicitOrg.toLowerCase() !== "internal") {
    return explicitOrg;
  }

  // 5. Check user groups association if applicable (e.g. ADL Engineer, etc.)
  const groups = Array.isArray(profile?.groups) ? profile.groups : [];
  const primaryGroup = profile?.primary_group;
  const allGroups = primaryGroup ? [primaryGroup, ...groups] : groups;

  for (const g of allGroups) {
    const gName = String(typeof g === "string" ? g : g?.name || "").trim();
    if (/adl/i.test(gName)) {
      return "Arkade Developers Limited";
    }
  }

  // 6. If project has a valid display name and user is internal to it
  if (projectCandidateName && typeof projectCandidateName === "string") {
    const cleanProj = projectCandidateName.trim();
    if (
      cleanProj &&
      !/^project\s*#?\s*\d+$/i.test(cleanProj) &&
      cleanProj.toLowerCase() !== "internal" &&
      cleanProj.toLowerCase() !== "none"
    ) {
      return cleanProj;
    }
  }

  // 7. CASE 5: User genuinely has no organization, contractor, or group association
  return "Internal";
};

/**
 * Determines whether the active user is an Internal ADL Supervisor.
 *
 * Requirements:
 * 1. User must be Internal (user_type is "INTERNAL" or not explicitly "EXTERNAL").
 * 2. User must belong to the ADL Supervisor group or hold an ADL Supervisor role.
 *
 * @param {Object} [userProfile] - Profile resolved from resolveSafetyUserProfile()
 * @param {Object} [userData] - Profile resolved from getUserData()
 * @returns {boolean}
 */
export const isInternalADLSupervisor = (userProfile = null, userData = null) => {
  const profile = userProfile || userData || {};

  // 1. User type check: must not be external
  const userType = String(
    profile.user_type || profile.userType || ""
  ).trim().toUpperCase();

  if (userType === "EXTERNAL") {
    return false;
  }

  // If user has an explicit external contractor assigned, not an internal ADL supervisor
  const contractorName = String(
    profile.contractor_name || profile.contractor || ""
  ).trim().toLowerCase();
  if (
    contractorName &&
    contractorName !== "internal" &&
    contractorName !== "assigned to internal team" &&
    !contractorName.includes("arkade") &&
    !contractorName.includes("adl")
  ) {
    return false;
  }

  // 2. Collect all group codes, group names, and roles
  const candidateCodes = [];
  const candidateNames = [];

  const addCandidate = (code, name) => {
    if (code) candidateCodes.push(String(code).trim().toUpperCase());
    if (name) candidateNames.push(String(name).trim().toLowerCase());
  };

  addCandidate(profile.group_code || profile.groupCode, profile.group_name || profile.groupName);

  if (profile.primary_group) {
    if (typeof profile.primary_group === "object") {
      addCandidate(profile.primary_group.code, profile.primary_group.name);
    } else if (typeof profile.primary_group === "string") {
      candidateCodes.push(profile.primary_group.trim().toUpperCase());
      candidateNames.push(profile.primary_group.trim().toLowerCase());
    }
  }

  if (Array.isArray(profile.groups)) {
    profile.groups.forEach((g) => {
      if (typeof g === "object" && g !== null) {
        addCandidate(g.code, g.name);
      } else if (typeof g === "string") {
        candidateCodes.push(g.trim().toUpperCase());
        candidateNames.push(g.trim().toLowerCase());
      }
    });
  }

  const roles = Array.isArray(profile.roles) ? profile.roles : [];
  roles.forEach((r) => {
    const rCode = typeof r === "string" ? r : r?.code || r?.role || r?.role_name || r?.name || "";
    const rName = typeof r === "object" && r !== null ? r?.name || r?.label || "" : "";
    addCandidate(rCode, rName);
  });

  const activeRole = String(
    (typeof localStorage !== "undefined" && (
      localStorage.getItem("FLOW_ROLE") ||
      localStorage.getItem("ACTIVE_ROLE") ||
      localStorage.getItem("ROLE")
    )) || ""
  ).trim().toUpperCase();
  if (activeRole) {
    candidateCodes.push(activeRole);
  }

  // 3. Match ADL Supervisor codes and names
  const adlSupervisorCodes = [
    "ADL_SUPERVISOR",
    "ADL_SUPERVISORY",
    "ADL_SAFETY_SUPERVISOR",
    "ADL_SITE_SUPERVISOR",
  ];

  for (const c of candidateCodes) {
    if (adlSupervisorCodes.includes(c) || (c.includes("ADL") && c.includes("SUPERVISOR"))) {
      return true;
    }
  }

  for (const n of candidateNames) {
    if (n.includes("adl") && n.includes("supervisor")) {
      return true;
    }
  }

  // 4. Fallback: If role is SUPERVISOR and organization is ADL / Arkade
  const orgName = String(
    profile.organization_name ||
    profile.org_name ||
    (typeof localStorage !== "undefined" && localStorage.getItem("ORG_NAME")) ||
    ""
  ).trim().toLowerCase();

  const hasSupervisorRole = candidateCodes.some(
    (c) => c === "SUPERVISOR" || c.includes("SUPERVISOR")
  );
  if (hasSupervisorRole && (orgName.includes("adl") || orgName.includes("arkade"))) {
    return true;
  }

  return false;
};


